import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Notif, Product } from "../types";

function timeAgo(iso: string): string {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return `${Math.floor(d)}s`;
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

function rowToNotif(row: any): Notif {
  return {
    id: row.id,
    type: row.type || "general",
    icon: row.type === "referral_reward" ? "🎁" : row.type === "price" ? "💰" : "🔔",
    read: !!row.is_read,
    time: timeAgo(row.created_at),
    title_key: "_raw_title" as any,
    title: row.title || "",
    prod: null as any,
    msg_key: "_raw_msg" as any,
    msg: row.message || "",
    action_url: row.action_url || null,
    _persisted: true,
  } as any;
}

/**
 * Notifications inbox. Loads persisted notifications from the DB for the
 * authenticated user (via realtime), and falls back to simulated seed entries
 * for guests.
 */
export function useNotifications(products: Product[]) {
  const [notifs, setNotifs] = useState<Notif[]>(() => [
    { id: "n1", type: "price", icon: "💰", read: false, time: "2h", title_key: "notifPriceDrop", prod: products[0], msg_key: "notifPriceMsg" },
    { id: "n2", type: "streak", icon: "🔥", read: false, time: "5h", title_key: "notifStreak", prod: null as any, msg_key: "notifStreakMsg" },
    { id: "n3", type: "social", icon: "👥", read: true, time: "1d", title_key: "notifSocial", prod: null as any, msg_key: "notifSocialMsg", extra: "María S." },
    { id: "n4", type: "reorder", icon: "📦", read: true, time: "2d", title_key: "notifReorder2", prod: products.find((x) => x.id === 9) as any, msg_key: "notifReorderMsg" },
  ]);
  const [notifTab, setNotifTab] = useState<"all" | "unread">("all");
  const [showNotifs, setShowNotifs] = useState(false);

  // Load + subscribe to DB notifications when authenticated
  useEffect(() => {
    let unsub = () => {};
    let channel: any = null;
    let currentUid: string | null = null;

    const setup = async (uid: string) => {
      if (currentUid === uid && channel) return;
      currentUid = uid;
      if (channel) {
        try { supabase.removeChannel(channel); } catch {}
        channel = null;
      }
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", uid)
        .order("created_at", { ascending: false })
        .limit(20);
      if (data) {
        setNotifs((prev) => {
          const seeds = prev.filter((n: any) => !n._persisted);
          return [...data.map(rowToNotif), ...seeds];
        });
      }
      const ch = supabase.channel(`notif-${uid}-${Date.now()}`);
      channel = ch
        .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${uid}` }, (payload: any) => {
          setNotifs((prev) => {
            if (payload.eventType === "INSERT") return [rowToNotif(payload.new), ...prev];
            if (payload.eventType === "UPDATE") return prev.map((n: any) => n.id === payload.new.id ? rowToNotif(payload.new) : n);
            if (payload.eventType === "DELETE") return prev.filter((n: any) => n.id !== payload.old.id);
            return prev;
          });
        })
        .subscribe();
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session?.user?.id) {
        setup(session.user.id);
      } else {
        currentUid = null;
        if (channel) { try { supabase.removeChannel(channel); } catch {} channel = null; }
      }
    });
    unsub = () => {
      sub.subscription.unsubscribe();
      if (channel) supabase.removeChannel(channel);
    };
    return unsub;
  }, []);

  // Wrap setter to persist read-state changes
  const setNotifsPersisted = (updater: (prev: Notif[]) => Notif[]) => {
    setNotifs((prev) => {
      const next = updater(prev);
      // detect read changes for persisted notifs
      const changed = next.filter((n: any, i) => {
        const old = prev[i];
        return n._persisted && old && old.id === n.id && !old.read && n.read;
      });
      if (changed.length) {
        const ids = changed.map((n: any) => n.id);
        supabase.from("notifications").update({ is_read: true }).in("id", ids).then();
      }
      // detect deletions of persisted notifs
      const removed = prev.filter((p: any) => p._persisted && !next.find((n) => n.id === p.id));
      if (removed.length) {
        const ids = removed.map((n: any) => n.id);
        supabase.from("notifications").delete().in("id", ids).then();
      }
      return next;
    });
  };

  const deleteNotif = (id: any) =>
    setNotifsPersisted((prev) => prev.filter((n) => n.id !== id));

  return { notifs, setNotifs: setNotifsPersisted as typeof setNotifs, notifTab, setNotifTab, showNotifs, setShowNotifs, deleteNotif };
}